import React from 'react';

/**
 * Phase 15.3 - Skeleton Loading Components
 * Unified skeleton design for loading states
 */

// Base Skeleton
export const Skeleton = ({ className = '', ...props }) => {
  return (
    <div
      className={`animate-skeleton-pulse bg-gradient-to-r from-hayat-neutral-200 via-hayat-neutral-100 to-hayat-neutral-200 bg-[length:200%_100%] rounded-xl ${className}`}
      {...props}
    />
  );
};

// Skeleton Card
export const SkeletonCard = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-3xl p-6 shadow-hayat-md border border-hayat-neutral-100 ${className}`}>
      <div className="flex items-start gap-4">
        <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
};

// Skeleton List Item
export const SkeletonListItem = () => {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-hayat-md border border-hayat-neutral-100">
      <div className="flex items-center gap-4">
        <Skeleton className="w-16 h-16 rounded-2xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
        </div>
        <Skeleton className="w-8 h-8 rounded-xl" />
      </div>
    </div>
  );
};

// Skeleton Dashboard
export const SkeletonDashboard = () => {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-3xl p-6 shadow-hayat-md border border-hayat-neutral-100">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="w-12 h-12 rounded-2xl" />
              <Skeleton className="w-16 h-8 rounded-xl" />
            </div>
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-6 w-12" />
          </div>
        ))}
      </div>

      {/* Family Members */}
      <div>
        <Skeleton className="h-8 w-48 mb-6" />
        <SkeletonCard />
      </div>

      {/* Recent Activity */}
      <div>
        <Skeleton className="h-8 w-40 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Skeleton Grid (for Seniors page)
export const SkeletonGrid = ({ count = 3 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-3xl p-6 shadow-hayat-md border border-hayat-neutral-100">
          <div className="flex flex-col items-center">
            <Skeleton className="w-24 h-24 rounded-2xl mb-4" />
            <Skeleton className="h-6 w-32 mb-4" />
            <div className="space-y-2 w-full">
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Skeleton Activity Feed
export const SkeletonActivityFeed = ({ count = 5 }) => {
  return (
    <div className="space-y-4 animate-fade-in">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
};

// Skeleton Settings
export const SkeletonSettings = () => {
  return (
    <div className="space-y-6 animate-fade-in">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-3xl p-8 shadow-hayat-md border border-hayat-neutral-100">
          <Skeleton className="h-6 w-40 mb-4" />
          <div className="space-y-3">
            <Skeleton className="h-12 w-full rounded-2xl" />
            <Skeleton className="h-12 w-full rounded-2xl" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default Skeleton;
